import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BookTextIcon, LayersIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { messageForError } from '@/lib/api-client';
import { sitesQuery } from '@/lib/api/sites';

/**
 * Content is always scoped to one site, so this screen routes into a site
 * rather than pretending an organisation-wide editing surface exists.
 */
function ContentPage() {
  const { orgId } = Route.useParams();
  const sites = useQuery(sitesQuery(orgId));

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Workspace"
        title="Content"
        description="Every entry belongs to a site — and within it, to a page or the site-wide bucket. Pick the site you want to work on."
      />

      {sites.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2" aria-busy="true">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} className="h-40 w-full rounded-lg" />
          ))}
        </div>
      ) : null}

      {sites.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
        >
          {messageForError(sites.error, 'The site list could not be loaded.')}
        </div>
      ) : null}

      {!sites.isLoading && (sites.data?.length ?? 0) === 0 && !sites.isError ? (
        <EmptyState
          icon={LayersIcon}
          title="No sites to edit yet"
          description="Content lives inside a site. Connect one first, tag the regions editors should own, and the entries appear here."
          action={
            <PermissionGate permission={PERMISSIONS.SITE_VIEW}>
              <Button asChild>
                <Link to="/app/$orgId/sites" params={{ orgId }}>
                  Go to sites
                </Link>
              </Button>
            </PermissionGate>
          }
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        {(sites.data ?? []).map((site) => (
          <Card key={site.id}>
            <CardHeader>
              <span className="section-label">{site.slug}</span>
              <CardTitle>{site.name}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button asChild variant="secondary" size="sm">
                <Link
                  to="/app/$orgId/sites/$siteId/pages"
                  params={{ orgId, siteId: site.id }}
                >
                  <BookTextIcon />
                  Pages
                </Link>
              </Button>
              <Button asChild variant="secondary" size="sm">
                <Link
                  to="/app/$orgId/sites/$siteId/content"
                  params={{ orgId, siteId: site.id }}
                >
                  Site-wide content
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link
                  to="/app/$orgId/sites/$siteId/publish"
                  params={{ orgId, siteId: site.id }}
                >
                  Publish
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/content')({
  component: ContentPage,
});
